import React, { useState, useEffect } from 'react';
import { db, handleFirestoreError, OperationType } from '../../lib/firebase';
import { collection, onSnapshot, query, where, writeBatch, doc } from 'firebase/firestore';
import { ConventionMetadata, Prop, ConShift } from '../../types';
import { addDays, format, isBefore, isAfter, parseISO, addMinutes } from 'date-fns';
import { toZonedTime, fromZonedTime, format as formatTZ } from 'date-fns-tz';
import { toast } from 'sonner';
import { Zap, ShieldAlert, Cpu, Hammer, Rocket, Users } from 'lucide-react';
import { Input } from '../ui/input';

const TIME_ZONE = 'America/Chicago';

interface AutoSchedulerProps {
  convention: ConventionMetadata;
  existingShifts: ConShift[];
  onComplete: () => void;
}

export default function AutoScheduler({ convention, existingShifts, onComplete }: AutoSchedulerProps) {
  const [props, setProps] = useState<Prop[]>([]);
  const [selectedPropIds, setSelectedPropIds] = useState<string[]>([]);
  const [floatersPerDay, setFloatersPerDay] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const unsubProps = onSnapshot(collection(db, 'props'), (snap) => {
      setProps(snap.docs.map(d => ({ id: d.id, ...d.data() } as Prop)));
      if (convention.reservedPropIds) {
        setSelectedPropIds(convention.reservedPropIds);
      }
    });

    return () => {
      unsubProps();
    };
  }, [convention.startDate, convention.endDate, convention.reservedPropIds]);

  const toggleProp = (id: string) => {
    setSelectedPropIds(prev => 
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const handleAutoGenerate = async () => {
    if (selectedPropIds.length === 0 && floatersPerDay === 0) {
      toast.error('Select at least one Prop to schedule or assign floaters');
      return;
    }

    setIsProcessing(true);
    try {
      const batch = writeBatch(db);
      let shiftsCreated = 0;

      // Calculate total staff needed
      let totalStaffNeeded = floatersPerDay;
      for (const propId of selectedPropIds) {
        const prop = props.find(p => p.id === propId);
        if (prop) totalStaffNeeded += prop.staffNeeded;
      }

      // Ensure "General Convention Duty" prop exists
      let generalPropId = '';
      const existingGeneral = props.find(p => p.name === 'General Convention Duty');
      if (existingGeneral) {
        generalPropId = existingGeneral.id!;
      } else {
        const generalRef = doc(collection(db, 'props'));
        batch.set(generalRef, {
          name: 'General Convention Duty',
          description: 'Pooled staffing for all convention stations and backup duties.',
          staffNeeded: 0,
          inventoryItemIds: []
        });
        generalPropId = generalRef.id;
      }

      if (!convention.startDate || !convention.endDate) {
        toast.error('Date metadata incomplete. Please structure the date parameters first.');
        setIsProcessing(false);
        return;
      }

      // Iterate through each day of the convention
      const startDate = parseISO(convention.startDate);
      const endDate = parseISO(convention.endDate);
      
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
         toast.error('Invalid date format in convention parameters.');
         setIsProcessing(false);
         return;
      }
      
      let currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        const dateStr = format(currentDate, 'yyyy-MM-dd');
        const dayHours = convention.dailyHours[dateStr];

        if (dayHours && dayHours.open && dayHours.close) {
          // Parse times as Central Time
          const openingTime = fromZonedTime(`${dateStr}T${dayHours.open}:00`, TIME_ZONE);
          const closingTime = fromZonedTime(`${dateStr}T${dayHours.close}:00`, TIME_ZONE);

          const SHIFT_DURATION_MINS = 150; // 2.5 hours
          
          let shiftStartTime = new Date(openingTime);
          while (isBefore(shiftStartTime, closingTime)) {
            let shiftEndTime = addMinutes(shiftStartTime, SHIFT_DURATION_MINS);
            if (isAfter(shiftEndTime, closingTime)) {
              shiftEndTime = new Date(closingTime);
            }

            // Create a single pooled shift per timeslot
            const shiftRef = doc(collection(db, 'shifts'));
            const newShift: ConShift = {
              conId: convention.id!,
              propId: generalPropId,
              startTime: shiftStartTime.toISOString(),
              endTime: shiftEndTime.toISOString(),
              assignedUserIds: [],
              maxVolunteers: totalStaffNeeded
            };
            batch.set(shiftRef, newShift);
            shiftsCreated++;

            shiftStartTime = addMinutes(shiftStartTime, 150); // Next interval
          }
        }
        currentDate = addDays(currentDate, 1);
      }

      await batch.commit();
      
      if (shiftsCreated > 0) {
        toast.success(`Automated deployment complete: ${shiftsCreated} shifts established.`);
        onComplete();
      } else {
        toast.error('Strategic failure: No shifts could be generated within constraints.');
      }
    } catch (err) {
      handleFirestoreError(err, OperationType.WRITE, 'shifts');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="bg-lcars-blue/10 border-2 border-lcars-blue p-6 rounded-[40px] space-y-6">
      <div className="flex items-center gap-4 border-b border-lcars-blue/30 pb-4">
        <Zap className="w-10 h-10 text-lcars-blue animate-pulse" />
        <div>
          <h3 className="text-2xl font-bold text-lcars-blue uppercase leading-none">Automated Shift Engine</h3>
          <p className="text-[0.6rem] font-bold text-[var(--foreground)]/40 uppercase tracking-widest mt-1">Manning Shift Generator v4.3</p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="text-[0.6rem] font-bold text-lcars-blue uppercase tracking-widest ml-4">Select Propulsion Systems (Props) to Deploy</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {(!convention.reservedPropIds || convention.reservedPropIds.length === 0) && (
            <div className="col-span-3 text-[var(--foreground)]/40 text-center py-8 uppercase text-sm">
              No scenes reserved for this operation. Please revise details in setup.
            </div>
          )}
          {props.filter(p => p.name !== 'General Convention Duty' && convention.reservedPropIds?.includes(p.id!)).map(prop => (
            <button
              key={prop.id}
              onClick={() => toggleProp(prop.id!)}
              className={`p-4 rounded-xl border text-left transition-all relative overflow-hidden ${
                selectedPropIds.includes(prop.id!)
                  ? 'bg-lcars-blue border-lcars-blue text-black'
                  : 'bg-white/5 border-white/10 text-[var(--foreground)]/60 hover:border-lcars-blue'
              }`}
            >
              <div className="flex justify-between items-start mb-2">
                <Hammer className="w-4 h-4 opacity-40" />
                <span className="text-[0.6rem] font-bold uppercase">{prop.staffNeeded} CREW</span>
              </div>
              <div className="font-bold text-sm uppercase truncate">{prop.name}</div>
              <div className="text-[0.5rem] mt-1 opacity-60 line-clamp-1">{prop.description}</div>
              {selectedPropIds.includes(prop.id!) && (
                <ShieldAlert className="absolute -bottom-2 -right-2 w-8 h-8 opacity-20" />
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-[var(--background)]/60 p-6 rounded-2xl border border-white/10 flex items-center justify-between gap-6">
         <div className="flex items-center gap-3">
           <Users className="w-8 h-8 text-lcars-blue" />
           <div>
             <h4 className="text-sm font-bold text-lcars-blue uppercase">Floaters / Backup Personnel</h4>
             <p className="text-[0.6rem] text-[var(--foreground)]/40 font-bold uppercase tracking-widest">Reserve staffing capacity per shift block</p>
           </div>
         </div>
         <Input 
           type="number"
           min="0"
           max="20"
           value={floatersPerDay}
           onChange={(e) => setFloatersPerDay(parseInt(e.target.value) || 0)}
           className="w-24 bg-[var(--background)] border-lcars-blue text-[var(--foreground)] tabular-nums text-lg text-center font-bold"
         />
      </div>

      <div className="bg-[var(--background)]/60 p-4 rounded-2xl border border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-lcars-gold">
            <Cpu className="w-4 h-4" />
            <span className="text-xs font-bold uppercase tracking-widest">Operational Parameters</span>
          </div>
          <p className="text-[0.6rem] text-[var(--foreground)]/40 font-bold uppercase">
            Shifts: 2.5 Hours | Stagger: Linear
          </p>
        </div>
        
        <button
          onClick={handleAutoGenerate}
          disabled={isProcessing || (selectedPropIds.length === 0 && floatersPerDay === 0)}
          className={`lcars-pill px-8 py-3 font-bold text-sm uppercase flex items-center gap-2 transition-all ${
            isProcessing ? 'bg-lcars-tan opacity-50' : 'bg-lcars-blue text-black hover:scale-105 active:scale-95'
          }`}
        >
          {isProcessing ? 'CALCULATING...' : <><Rocket className="w-5 h-5" /> Execute Deployment</>}
        </button>
      </div>
    </div>
  );
}
